/**
 * `taskCategoryLibrary` — per-org user-extensible task buckets
 * (Electrical, Plumbing, Woodwork, …) used in project tasks.
 * Merged with `DEFAULT_TASK_CATEGORIES` at the form layer.
 *
 * Mirror of `src/features/tasks/taskCategories.ts` on mobile —
 * same Firestore path, same doc-id strategy (`${orgId}_${key}`)
 * for idempotent create.
 *
 * Permission: read = any org member; write = `canManageProjects`
 * (SA / Admin / Manager) per firestore.rules.
 */
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useMemo, useState } from 'react';

import { db } from '@/lib/firebase';

// ───── Built-in roster ────────────────────────────────────
//
// `TaskCategoryOption`, `DEFAULT_TASK_CATEGORIES`, and
// `toCategoryKey` now live in `@shared/perms/task` so mobile +
// web share one copy. Imported here for local use AND re-exported
// so existing imports from this module keep working.
import {
  DEFAULT_TASK_CATEGORIES,
  toCategoryKey,
  type TaskCategoryOption,
} from '@shared/perms';
export type { TaskCategoryOption };
export { DEFAULT_TASK_CATEGORIES, toCategoryKey };

// ───── Doc shape ──────────────────────────────────────────

export type TaskCategoryLibraryItem = {
  id: string;
  orgId: string;
  key: string;
  label: string;
  normalizedLabel: string;
  createdBy: string;
  createdAt: { toMillis(): number } | null;
};

// ───── CRUD ────────────────────────────────────────────────

export async function createTaskCategory(input: {
  orgId: string;
  label: string;
  createdBy: string;
}): Promise<string> {
  const cleanLabel = input.label.trim();
  if (!cleanLabel) throw new Error('Category label is required.');
  const normalized = cleanLabel.toLowerCase();
  const key = toCategoryKey(cleanLabel);
  const id = `${input.orgId}_${key}`;
  await setDoc(
    doc(db, 'taskCategoryLibrary', id),
    {
      orgId: input.orgId,
      key,
      label: cleanLabel,
      normalizedLabel: normalized,
      createdBy: input.createdBy,
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );
  return id;
}

export async function deleteTaskCategory(id: string): Promise<void> {
  await deleteDoc(doc(db, 'taskCategoryLibrary', id));
}

// ───── Live read hook ──────────────────────────────────────

export type UseTaskCategoriesResult = {
  /** Merged: defaults + the org's customs, de-duped by key, sorted by label. */
  data: TaskCategoryOption[];
  /** Raw library docs (customs only) so callers can find ids to delete. */
  customs: TaskCategoryLibraryItem[];
  loading: boolean;
};

export function useTaskCategories(
  orgId: string | null | undefined,
): UseTaskCategoriesResult {
  const [library, setLibrary] = useState<TaskCategoryLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgId) {
      setLibrary([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    return onSnapshot(
      query(collection(db, 'taskCategoryLibrary'), where('orgId', '==', orgId)),
      (snap) => {
        const rows: TaskCategoryLibraryItem[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<TaskCategoryLibraryItem, 'id'>),
        }));
        rows.sort((a, b) => a.label.localeCompare(b.label));
        setLibrary(rows);
        setLoading(false);
      },
      (err) => {
        console.warn('[useTaskCategories] snapshot error:', err);
        setLibrary([]);
        setLoading(false);
      },
    );
  }, [orgId]);

  const data = useMemo(() => {
    const map = new Map<string, TaskCategoryOption>();
    for (const item of DEFAULT_TASK_CATEGORIES) map.set(item.key, item);
    for (const item of library) map.set(item.key, { key: item.key, label: item.label });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [library]);

  return { data, customs: library, loading };
}
